// PricePAL Compare — Main App Router
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { loadCanonicalLabels } from './utils/canonicalLabels';
import Analytics from './utils/analytics';
import AdminApp from './AdminApp';
import UserApp from './UserApp';
import Homepage from './pages/Homepage';
import PortalPage from './pages/PortalPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutPage from './pages/AboutPage';
import PricingPage from './pages/PricingPage';
import BlogPage from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';

import ResetPasswordPage from './pages/ResetPasswordPage';
import ReviewerAccessPage from './pages/ReviewerAccessPage';
import NewsletterPreferencePage from './pages/NewsletterPreferencePage';
import CommunityLandingPage from './pages/CommunityLandingPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import FAQPage from './pages/FAQPage';
import RegisterPage from './pages/RegisterPage';
import PriceMapPage from './pages/PriceMapPage';
import MarketingAttributionCheckPage from './pages/MarketingAttributionCheckPage';
import AIChatWidget from './components/AIChatWidget';
import PricePALAssistant from './components/PricePALAssistant';
import ErrorBoundary from './components/ErrorBoundary';
import Toast from './components/Toast';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import CEODashboard from './components/CRM/CEODashboard';
import SkipNavigation from './components/SkipNavigation';
import A11yLiveRegion, { announce } from './components/A11yLiveRegion';
import CookieConsentBanner from './components/CookieConsentBanner';
import FuelPriceDeltaToast from './components/FuelPriceDeltaToast';
import './App.css';

// Layout: Public marketing pages (has chat widget)
function PublicLayout({ children, showChat = true }) {
  return (
    <>
      <SkipNavigation />
      <A11yLiveRegion />
      <ErrorBoundary>
        <main id="main-content">{children}</main>
        {showChat && <AIChatWidget />}
      </ErrorBoundary>
      <Toast />
      <CookieConsentBanner />
    </>
  );
}

// Layout: Minimal (legal pages, reset, newsletter)
function MinimalLayout({ children }) {
  return (
    <>
      <SkipNavigation />
      <A11yLiveRegion />
      <ErrorBoundary>
        <main id="main-content">{children}</main>
      </ErrorBoundary>
      <Toast />
    </>
  );
}

function App() {
  const [userId] = useState('user_1');
  const location = useLocation();

  // Load accessibility preferences on app start
  useEffect(() => {
    try {
      const savedA11y = localStorage.getItem('pricepal-accessibility');
      if (savedA11y) {
        const parsed = JSON.parse(savedA11y);
        if (parsed.highContrast) {
          document.documentElement.classList.add('high-contrast');
        }
      }
    } catch (e) { /* ignore */ }
    // Fetch canonical display labels once on boot (Law 2 — data consistency)
    loadCanonicalLabels().catch(() => { /* silent — defaults apply */ });

    // iter163 (2026-07-23) — Capture first-touch UTM/gclid/fbclid attribution
    // on ANY landing page (previously only Homepage.js triggered this). Newsletter
    // and social deep-links to /pricing, /community, /faq etc. now retain their
    // attribution. Idempotent — `captureFirstTouchAttribution` no-ops if a bundle
    // already exists in localStorage. Additive only: Google Ads gclid capture on
    // homepage remains unchanged, and no new events are emitted (GA4/GTM events
    // are untouched — this only stamps localStorage.pp_attribution).
    Analytics.captureFirstTouchAttribution();
  }, []);

  // Announce page changes to screen readers
  useEffect(() => {
    const pageNames = {
      '/': 'Home page',
      '/portal': 'Portal page',
      '/how-it-works': 'How It Works page',
      '/about': 'About page',
      '/pricing': 'Pricing page',
      '/blog': 'Blog page',
      '/contact': 'Contact page',
      '/privacy': 'Privacy Policy page',
      '/terms': 'Terms and Conditions page',
      '/cookies': 'Cookie Policy page',
      '/admin': 'Admin Dashboard',
      '/app': 'PricePAL Compare App',
    };
    const matched = Object.keys(pageNames).find(
      (p) => location.pathname.startsWith(p) && p !== '/'
    ) || '/';
    announce(`Navigated to ${pageNames[matched] || 'PricePAL Compare'}`);
  }, [location.pathname]);

  const hasRefCode = new URLSearchParams(location.search).has('ref');

  return (
    <Routes>
      {/* Public Marketing Pages */}
      <Route path="/" element={<PublicLayout><Homepage /></PublicLayout>} />
      <Route path="/portal" element={<MinimalLayout><PortalPage /></MinimalLayout>} />
      <Route path="/how-it-works" element={<PublicLayout><HowItWorksPage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/pricing" element={<PublicLayout><PricingPage userId={userId} /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/price-map" element={<PublicLayout><PriceMapPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      {/* Founder's Club removed — founder tiers now shown on /pricing page */}
      {/* Community landing page (unified — NHS, Blue Light, Student, Teacher).
          Audit 2026-02-12: consolidated /students + /teachers + /nhs into a
          single /community route. All legacy URLs 301-redirect, preserving
          inbound SEO + email links. The `type` query param pre-selects the
          right concession tab on arrival. */}
      <Route path="/community" element={<MinimalLayout><CommunityLandingPage /></MinimalLayout>} />
      <Route path="/nhs" element={<Navigate to="/community?type=nhs" replace />} />
      <Route path="/blue-light" element={<Navigate to="/community?type=blue_light" replace />} />
      <Route path="/bluelight" element={<Navigate to="/community?type=blue_light" replace />} />
      <Route path="/students" element={<Navigate to="/community?type=student" replace />} />
      <Route path="/teachers" element={<Navigate to="/community?type=teacher" replace />} />
      <Route path="/refund-policy" element={<PublicLayout><RefundPolicyPage /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />

      {/* iter163 (2026-07-23) — Hidden marketing-attribution debug page.
          Not linked from anywhere. Owner bookmarks the URL. Read-only —
          only touches localStorage.pp_attribution; no writes elsewhere. */}
      <Route path="/marketing/attribution-check" element={<MinimalLayout><MarketingAttributionCheckPage /></MinimalLayout>} />

      {/* Legal Pages */}
      <Route path="/privacy" element={<MinimalLayout><PrivacyPolicyPage /></MinimalLayout>} />
      <Route path="/terms" element={<MinimalLayout><TermsConditionsPage /></MinimalLayout>} />
      <Route path="/cookies" element={<MinimalLayout><CookiePolicyPage /></MinimalLayout>} />

      {/* Utility Pages (public, no auth) */}
      <Route path="/reset-password/*" element={<ErrorBoundary><main id="main-content"><ResetPasswordPage /></main></ErrorBoundary>} />
      <Route path="/newsletter/preferences/*" element={<ErrorBoundary><main id="main-content"><NewsletterPreferencePage /></main></ErrorBoundary>} />
      {/* Beauty register route hidden — H&B feature paused */}

      {/*
        iter158 (2026-07-07) — Dedicated registration page. Marketing CTAs
        route here instead of /app so we get a clean top-of-funnel URL for
        GA4, Meta and Google Ads. Existing /app route is untouched.
      */}
      <Route path="/register" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId={userId}>
            <main id="main-content"><RegisterPage /></main>
            <PricePALAssistant userId={userId} currentPage="/register" />
          </ErrorBoundary>
          <Toast />
          <CookieConsentBanner />
          <PWAInstallPrompt />
        </>
      } />

      {/* Reviewer / Trial Access */}
      <Route path="/reviewer" element={<ErrorBoundary><main id="main-content"><ReviewerAccessPage /></main></ErrorBoundary>} />
      <Route path="/review" element={<ErrorBoundary><main id="main-content"><ReviewerAccessPage /></main></ErrorBoundary>} />
      <Route path="/partner" element={<ErrorBoundary><main id="main-content"><ReviewerAccessPage /></main></ErrorBoundary>} />
      <Route path="/try" element={<ErrorBoundary><main id="main-content"><ReviewerAccessPage /></main></ErrorBoundary>} />

      {/* Beta Signup — direct to User Portal */}
      <Route path="/beta" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId={userId}>
            <main id="main-content"><UserApp isBetaSignup={true} /></main>
            <PricePALAssistant userId={userId} currentPage="/beta" />
          </ErrorBoundary>
          <Toast />
          <PWAInstallPrompt />
        </>
      } />
      <Route path="/beta-signup" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId={userId}>
            <main id="main-content"><UserApp isBetaSignup={true} /></main>
            <PricePALAssistant userId={userId} currentPage="/beta-signup" />
          </ErrorBoundary>
          <Toast />
          <PWAInstallPrompt />
        </>
      } />
      <Route path="/join" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId={userId}>
            <main id="main-content"><UserApp isBetaSignup={true} /></main>
            <PricePALAssistant userId={userId} currentPage="/join" />
          </ErrorBoundary>
          <Toast />
          <PWAInstallPrompt />
        </>
      } />

      {/* Admin Dashboard */}
      <Route path="/admin/*" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId="admin">
            <main id="main-content"><AdminApp /></main>
            <PricePALAssistant userId="admin_user" currentPage={location.pathname} />
          </ErrorBoundary>
          <Toast />
        </>
      } />

      {/* CRM Dashboard */}
      <Route path="/crm/*" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId="ceo">
            <main id="main-content"><CEODashboard /></main>
          </ErrorBoundary>
          <Toast />
        </>
      } />

      {/* User App */}
      <Route path="/app/*" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId={userId}>
            <main id="main-content"><UserApp isBetaSignup={hasRefCode} /></main>
            <PricePALAssistant userId={userId} currentPage={location.pathname} />
          </ErrorBoundary>
          <Toast />
          <PWAInstallPrompt />
          <CookieConsentBanner />
          {/* Phase 3+ enhancement (audit doc 11.06.2026): mounts ONCE at the
              route-shell level so it survives every UserApp early-return path. */}
          <FuelPriceDeltaToast />
        </>
      } />
      <Route path="/user/*" element={
        <>
          <SkipNavigation />
          <A11yLiveRegion />
          <ErrorBoundary userId={userId}>
            <main id="main-content"><UserApp isBetaSignup={hasRefCode} /></main>
            <PricePALAssistant userId={userId} currentPage={location.pathname} />
          </ErrorBoundary>
          <Toast />
          <PWAInstallPrompt />
          <CookieConsentBanner />
          <FuelPriceDeltaToast />
        </>
      } />

      {/* Catch-all: Homepage */}
      <Route path="*" element={<PublicLayout><Homepage /></PublicLayout>} />
    </Routes>
  );
}

export default App;
